import {
    CouponData,
    CouponApiResponse,
    CouponProvider,
} from '../types/campaign';
import { getApiBaseUrl } from '../utils/apiConfig';
import getConfig from 'next/config';
import logger from '../utils/logger';

const { publicRuntimeConfig } = getConfig() || {};

// Get API URL dynamically
const getApiUrl = () => getApiBaseUrl();

// Generate public token - reuse existing logic from api.ts
function generatePublicToken(): string {
    const ENV = process.env.NODE_ENV || 'development';
    const appCodeName = 'Mozilla';
    const appName = 'Netscape';
    const platform = 'Linux x86_64';

    let publicToken = ENV + appCodeName + appName + platform;

    publicToken = publicToken.replace(/M/g, () => {
        return Math.random().toString().substring(2, 6);
    });
    publicToken = publicToken.replace(/N/g, () => {
        return Math.random().toString().substring(2, 6);
    });
    publicToken = publicToken.replace(/d/g, () => {
        return Math.random().toString().substring(2, 6);
    });

    return publicToken;
}

// Get public token
function getPublicToken(): string {
    return (
        publicRuntimeConfig?.niioConfig?.app?.apiTokenPublic ||
        process.env.NEXT_PUBLIC_API_TOKEN_PUBLIC ||
        generatePublicToken()
    );
}

// Get JWT token - prioritize user's JWT from localStorage if available
function getJwtToken(): string {
    // First, check if we have a user JWT token in localStorage (from logged-in user)
    if (typeof window !== 'undefined') {
        const userJwtToken = localStorage.getItem('jwtToken');
        if (userJwtToken) {
            return userJwtToken;
        }
    }

    // Fall back to config/env tokens (for anonymous users)
    return (
        publicRuntimeConfig?.niioConfig?.app?.apiToken ||
        process.env.NEXT_PUBLIC_API_TOKEN ||
        ''
    );
}

/**
 * Detects the provider based on the device type
 *
 * Pair is a WEB app - always use Chargebee for billing.
 * Even on iOS Safari, we use Chargebee + Apple Pay (web payment).
 * iOS plan IDs are only for the native iOS app (App Store / In-App Purchase).
 */
export function detectProvider(): CouponProvider {
    // Pair web app always uses Chargebee
    return 'charge_bee';

    /*
     * Commented out: iOS detection logic for potential future native app use
     *
     * if (typeof window === 'undefined') {
     *     return 'charge_bee'; // Default for SSR
     * }
     *
     * const userAgent = (navigator.userAgent || '').toLowerCase();
     * const platform = navigator.platform || '';
     *
     * // Check for real iOS devices
     * // On actual iOS devices, navigator.platform will be exactly one of these strings
     * const isRealIOS = ['iPhone', 'iPad', 'iPod'].includes(platform);
     *
     * // Additional validation: iOS devices should have 'mobile' in user agent
     * // and should NOT have 'intel' (which indicates Mac with Intel processor)
     * // Also check for Chrome DevTools emulation
     * const isEmulated =
     *     userAgent.includes('chrome') && userAgent.includes('mobile');
     *
     * const hasIOSCharacteristics =
     *     isRealIOS &&
     *     userAgent.includes('mobile') &&
     *     !userAgent.includes('intel') &&
     *     !isEmulated; // Exclude Chrome DevTools emulation
     *
     * // MacBooks will have platform like 'MacIntel' and won't pass the above checks
     *
     * logger.debug('[COUPON-SERVICE] Provider detection:', {
     *     platform,
     *     userAgent: userAgent.substring(0, 100), // Log first 100 chars for debugging
     *     isRealIOS,
     *     isEmulated,
     *     hasIOSCharacteristics,
     *     provider: hasIOSCharacteristics ? 'apple' : 'charge_bee',
     * });
     *
     * return hasIOSCharacteristics ? 'apple' : 'charge_bee';
     */
}

/**
 * Fetches coupon details from the API
 * @param couponName - The coupon name from the query parameter
 * @param provider - Optional provider override, otherwise auto-detected
 */
export async function getCouponByCode(
    couponName: string,
    provider?: CouponProvider
): Promise<CouponApiResponse> {
    if (!couponName || !couponName.trim()) {
        return {
            success: false,
            error: 'Coupon name is required',
        };
    }

    const detectedProvider = provider || detectProvider();
    const fullUrl = `${getApiUrl()}/coupons/getCouponByCode/${detectedProvider}/${encodeURIComponent(
        couponName.trim()
    )}`;

    logger.debug('[COUPON-SERVICE] Fetching coupon:', {
        couponName,
        provider: detectedProvider,
        url: fullUrl,
    });

    try {
        const response = await fetch(fullUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-MyApiTokenHeaderPublic': getPublicToken(),
                'X-MyApiTokenHeader': getJwtToken(),
            },
            credentials: 'include',
        });

        logger.debug(
            '[COUPON-SERVICE] API Response:',
            response.status,
            response.statusText
        );

        if (!response.ok) {
            const errorText = await response
                .text()
                .catch(() => 'Unknown error');
            logger.error('[COUPON-SERVICE] API Error: ' + errorText);

            // Check for JWT validation error (user needs to re-authenticate)
            if (
                response.status === 400 &&
                errorText.includes('JWT Not validated')
            ) {
                logger.debug(
                    '[COUPON-SERVICE] JWT token invalid or expired, user needs to re-authenticate'
                );
                return {
                    success: false,
                    error: 'Session expired. Please sign in again.',
                    requiresAuth: true, // Special flag to indicate re-authentication needed
                };
            }

            // Handle specific error cases
            if (response.status === 404) {
                return {
                    success: false,
                    error: 'Coupon not found or has expired',
                };
            }

            return {
                success: false,
                error: `Failed to fetch coupon: ${response.status} ${response.statusText}`,
            };
        }

        const responseData = await response.json();
        logger.debug('[COUPON-SERVICE] API response received:', responseData);

        // Extract the coupon data from the response
        // The API returns data nested under 'coupon' property
        const data = responseData.coupon || responseData;

        // Validate the response structure
        if (!data || typeof data !== 'object') {
            logger.error(
                '[COUPON-SERVICE] Invalid data structure:',
                responseData
            );
            return {
                success: false,
                error: 'Invalid coupon data received',
            };
        }

        logger.debug('[COUPON-SERVICE] Extracted coupon data:', data);
        logger.debug(
            '[COUPON-SERVICE] Marketing screen data:',
            data.metadata_display_properties?.marketingScreen
        );

        // Transform the data to match our interface
        const couponData: CouponData = {
            id: data.id || couponName,
            name: data.name || couponName,
            provider: detectedProvider,
            discount_percent: data.discount_percent,
            discount_amount: data.discount_amount,
            expires_at: data.expires_at || data.end,
            metadata_display_properties: data.metadata_display_properties,
            is_expired: data.is_expired,
            is_used: data.is_used,
            // Include any additional properties
            ...data,
        };

        return {
            success: true,
            data: couponData,
        };
    } catch (error) {
        logger.error(
            '[COUPON-SERVICE] Network error',
            error instanceof Error ? error : undefined
        );
        return {
            success: false,
            error:
                error instanceof Error
                    ? error.message
                    : 'Network error occurred',
        };
    }
}

/**
 * Fetches all default coupons for a provider
 * These are coupons marked as 'default' in the database and are used
 * to fill gaps when user's specific coupon doesn't apply to all plan/cycle combinations
 * @param provider - Optional provider override, otherwise auto-detected
 */
export async function getDefaultCoupons(
    provider?: CouponProvider
): Promise<CouponData[]> {
    const detectedProvider = provider || detectProvider();
    const fullUrl = `${getApiUrl()}/coupons/getDefaultCoupons/${detectedProvider}`;

    logger.debug('[COUPON-SERVICE] Fetching default coupons:', {
        provider: detectedProvider,
        url: fullUrl,
    });

    try {
        const response = await fetch(fullUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-MyApiTokenHeaderPublic': getPublicToken(),
                'X-MyApiTokenHeader': getJwtToken(),
            },
            credentials: 'include',
        });

        logger.debug(
            '[COUPON-SERVICE] Default coupons API Response:',
            response.status,
            response.statusText
        );

        if (!response.ok) {
            logger.warn(
                '[COUPON-SERVICE] Failed to fetch default coupons:',
                response.status
            );
            return [];
        }

        const responseData = await response.json();
        logger.debug(
            '[COUPON-SERVICE] Default coupons received:',
            responseData
        );

        // Extract coupons array from response
        const coupons = responseData.coupons || [];

        // Transform each coupon to match our interface
        return coupons.map((data: Record<string, unknown>) => ({
            id: data.id as string,
            name: data.name as string,
            provider: detectedProvider,
            discount_percent: data.discount_percent as number | undefined,
            discount_amount: data.discount_amount as number | undefined,
            expires_at: (data.expires_at || data.end) as string | undefined,
            metadata_display_properties: data.metadata_display_properties as
                | Record<string, unknown>
                | undefined,
            is_expired: data.is_expired as boolean | undefined,
            is_used: data.is_used as boolean | undefined,
            account_type: data.account_type as
                | 'personal'
                | 'business'
                | undefined,
            user_plan_id: data.user_plan_id as string | undefined,
            payment_cycle: data.payment_cycle as
                | 'monthly'
                | 'annually'
                | undefined,
            coupon_code: data.coupon_code as string | undefined,
            ...data,
        })) as CouponData[];
    } catch (error) {
        logger.error(
            '[COUPON-SERVICE] Error fetching default coupons',
            error instanceof Error ? error : undefined
        );
        return [];
    }
}

/**
 * Validates if coupon data has required marketing screen properties
 */
export function hasMarketingScreen(couponData: CouponData | null): boolean {
    return !!(
        couponData?.metadata_display_properties?.marketingScreen &&
        couponData.metadata_display_properties.marketingScreen.title &&
        couponData.metadata_display_properties.marketingScreen.description
    );
}

/**
 * Gets the marketing screen data from coupon data
 */
export function getMarketingScreen(couponData: CouponData | null) {
    return couponData?.metadata_display_properties?.marketingScreen || null;
}

/**
 * Checks if a coupon is expired
 */
export function isCouponExpired(couponData: CouponData): boolean {
    if (!couponData.expires_at) {
        return false; // No expiration date means it's valid
    }

    try {
        const expirationDate = new Date(couponData.expires_at);
        const now = new Date();
        return now > expirationDate;
    } catch (error) {
        logger.warn(
            '[COUPON-SERVICE] Invalid expiration date format:',
            couponData.expires_at
        );
        return false; // If we can't parse the date, assume it's valid
    }
}

/**
 * Formats the coupon discount for display
 */
export function formatCouponDiscount(couponData: CouponData): string {
    if (couponData.discount_percent) {
        return `${couponData.discount_percent}% off`;
    }

    if (couponData.discount_amount) {
        return `$${couponData.discount_amount} off`;
    }

    return 'Special offer';
}

export default {
    getCouponByCode,
    getDefaultCoupons,
    detectProvider,
    hasMarketingScreen,
    getMarketingScreen,
    isCouponExpired,
    formatCouponDiscount,
};
